import { useQuery } from '@tanstack/react-query';
import type { FeatureCollection, MultiPolygon, Polygon } from 'geojson';
import { useMemo } from 'react';
import { Layer, Source } from 'react-map-gl';

import type { AssignmentDepartmentShare } from '../../data-access/types/gp-sampling-assignment';

import { ASSIGNMENT_POINTS_LAYER_ID } from './assignment-points-layer';

const NO_SHARES: AssignmentDepartmentShare[] = [];

const SOURCE_ID = 'assignment-department-shares';

type FranceDepartmentsGeoJson = FeatureCollection<
  Polygon | MultiPolygon,
  { code?: string; fill_color?: string; nom?: string }
>;

function fillColorForShare(share: AssignmentDepartmentShare): string {
  const dominant = share.shares.find(
    (slice) => slice.sampler_organization_id === share.dominant_sampler_organization_id,
  );
  return dominant?.color ?? share.shares[0]?.color ?? '#ced4da';
}

function normalizeDepartmentCode(code: string): string {
  const value = code.trim().toUpperCase();
  if (/^\d+$/.test(value)) {
    return value.padStart(2, '0');
  }
  return value;
}

export function buildDepartmentShareGeoJson(
  geojson: FranceDepartmentsGeoJson,
  shares: AssignmentDepartmentShare[],
): FranceDepartmentsGeoJson {
  const byCode = new Map(
    shares.map((share) => [normalizeDepartmentCode(share.code), share]),
  );

  return {
    type: 'FeatureCollection',
    features: geojson.features.flatMap((feature) => {
      const code = normalizeDepartmentCode(String(feature.properties?.code ?? ''));
      const share = byCode.get(code);
      if (!share) {
        return [];
      }
      return [
        {
          ...feature,
          properties: {
            code,
            fill_color: fillColorForShare(share),
          },
        },
      ];
    }),
  };
}

async function loadFranceDepartmentsGeoJson(): Promise<FranceDepartmentsGeoJson> {
  const response = await fetch('/geo/fr-departments.geojson');
  if (!response.ok) {
    throw new Error('Failed to load France departments GeoJSON');
  }
  return response.json();
}

export function DepartmentShareOverlay({
  shares,
}: {
  shares: AssignmentDepartmentShare[];
}) {
  const rows = shares.length === 0 ? NO_SHARES : shares;
  const geoQuery = useQuery({
    enabled: rows.length > 0,
    queryFn: loadFranceDepartmentsGeoJson,
    queryKey: ['geo', 'fr-departments'],
    staleTime: Infinity,
  });

  const data = useMemo(() => {
    if (!geoQuery.data) {
      return null;
    }
    return buildDepartmentShareGeoJson(geoQuery.data, rows);
  }, [geoQuery.data, rows]);

  if (!data || data.features.length === 0) {
    return null;
  }

  return (
    <Source data={data} id={SOURCE_ID} type="geojson">
      <Layer
        beforeId={ASSIGNMENT_POINTS_LAYER_ID}
        id="assignment-department-share-fill"
        paint={{
          'fill-color': ['get', 'fill_color'],
          'fill-opacity': 0.38,
        }}
        type="fill"
      />
      <Layer
        beforeId={ASSIGNMENT_POINTS_LAYER_ID}
        id="assignment-department-share-outline"
        paint={{
          'line-color': '#f8f9fa',
          'line-width': 1,
        }}
        type="line"
      />
    </Source>
  );
}
